;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type {
  CountryFieldScript,
  DATE_RANGE_TRANSFORM_UNITS,
  DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS,
  DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS,
  DynamicCalculationType,
  DynamicFieldType,
} from '../lib/constants';

/**
 * Conditionally shows and requires this field based on the value of a sibling field.
 *
 * When the watched `field` satisfies the `condition`, two things happen:
 *   1. The field becomes visible in the form (hidden otherwise).
 *   2. The field becomes required — an empty value produces a validation error.
 *
 * - `condition: "equals"` matches when the watched field equals `value`.
 * - `condition: "in"` matches when the watched field equals any entry in `values`.
 *
 * Example — show `end_date` only when `contract_type` is `"fixed"`:
 * ```json
 * {
 *   "field": "contract_type",
 *   "condition": "equals",
 *   "value": "fixed",
 *   "message_key": "validation.end_date_required"
 * }
 * ```
 *
 * Example — show `visa_number` when `residency_status` is `"visa"` or `"permit"`:
 * ```json
 * {
 *   "field": "residency_status",
 *   "condition": "in",
 *   "values": ["visa", "permit"]
 * }
 * ```
 *
 * `message_key` is an optional i18n key for a custom validation error message.
 * Falls back to `"<label> is required"` when omitted.
 */
interface ConditionalRequiredEquals {
  field: string;
  condition: typeof DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.EQUALS;
  value: string;
  message_key?: string;
}

interface ConditionalRequiredIn {
  field: string;
  condition: typeof DYNAMIC_FIELD_CONDITIONAL_REQUIRED_CONDITIONS.IN;
  values: string[];
  message_key?: string;
}

type ConditionalRequired = ConditionalRequiredEquals | ConditionalRequiredIn;

type ConditionalDependsUnits = 'months' | 'days';
type ConditionalDependsSelectValueRules = Record<string, number[]>;
type ConditionalDependsDateValueRules = Record<string, number>;

export interface ConditionalDependsValueRules {
  min_length?: number;
  max_length?: number;
}

/**
 * Constrains a field based on the computed diff (months or days) between two date fields
 * (`fields[0]` = from, `fields[1]` = to). The first threshold key where `diff <= key` wins;
 * falls back to `"default"`. For SELECT rules, `"default"` is also used when either date
 * value is missing.
 *
 * - `select_value_rules` — array of allowed option IDs for SELECT fields.
 * - `date_value_rules` — day offset from `fields[0]` used as the max selectable date for DATE fields.
 */
interface ConditionalDependsRange {
  condition: typeof DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.LESS_THAN;
  fields: string[];
  unit: ConditionalDependsUnits;
  select_value_rules?: ConditionalDependsSelectValueRules;
  date_value_rules?: ConditionalDependsDateValueRules;
}

/**
 * Constrains a field based on the exact value of `fields[0]`. The source field's value is
 * used as the lookup key; falls back to `"default"`. Boolean form values are coerced to
 * `"0"` / `"1"` before lookup.
 *
 * - `value_rules` — `min_length`/`max_length` bounds for TEXT fields, keyed by the source
 *   value (e.g. `document_type` → passport vs id_card number lengths). When present and no
 *   rule resolves (source not yet selected, no `"default"`), the input is disabled.
 * - `select_value_rules` — array of allowed option IDs for SELECT fields, keyed by the
 *   source value (e.g. `contract_type` → allowed probation periods).
 */
interface ConditionalDependsEquals {
  condition: typeof DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.EQUALS;
  fields: string[];
  value_rules?: Record<string, ConditionalDependsValueRules>;
  select_value_rules?: ConditionalDependsSelectValueRules;
}

type ConditionalDepends = ConditionalDependsRange | ConditionalDependsEquals;

interface CountryFieldNoteShowWhen {
  condition: typeof DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.IN;
  field: string;
  values: string[];
}

export interface CountryFieldNote {
  field: string;
  note: string;
  show_when?: CountryFieldNoteShowWhen;
  tooltip?: boolean;
}

/**
 * Derives the field value deterministically from a reference date field
 * (identified by `start_date_ref` on the parent `CountryField`).
 *
 * When present, the FE **auto-fills and locks** the field — no user input
 * is expected or accepted.
 *
 * Currently the only supported `type` is `"add_years_subtract_days"`:
 *   result = start_date_ref + `years` years − `days` days
 *
 * Example — a 1-year fixed contract where end date = start + 1 year − 1 day:
 * ```json
 * {
 *   "type": "add_years_subtract_days",
 *   "years": 1,
 *   "days": 1
 * }
 * ```
 * → If start_date is 2025-01-01, end_date is auto-set to 2025-12-31 (read-only).
 */
interface ConditionalCalculation {
  type: DynamicCalculationType;
  years?: number;
  days?: number;
}

/**
 * When present, the field renders as a date picker but is **submitted** as the
 * numeric length of the range between `from_field` and `to_field`.
 *
 * `to_field: "self"` means this field's own selected date value is used as
 * the upper bound of the range (the most common case).
 *
 * Example — probation period where the picker shows an end date but the API
 * expects a day count relative to `start_date`:
 * ```json
 * { "unit": "days", "from_field": "start_date", "to_field": "self" }
 * ```
 */
interface DateRangeTransform {
  unit: (typeof DATE_RANGE_TRANSFORM_UNITS)[keyof typeof DATE_RANGE_TRANSFORM_UNITS];
  /** Normalised form field name whose date is the range start. */
  from_field: string;
  /**
   * Normalised form field name for the range end, or `"self"` to use this
   * field's own picked date as the upper bound.
   */
  to_field: string;
}

/**
 * Absolute (rolling-from-today) date picker constraints for dynamic DATE fields.
 *
 * Past axis (ago):
 *   today - max_*_ago  ≤  selected  ≤  today - min_*_ago
 *
 * Future axis (ahead):
 *   selected  ≤  today + max_*_ahead
 *
 * - `min_*_ago` — date must be at least N units in the past (e.g. birth_date ≥ 18 years)
 * - `max_*_ago` — date must be at most N units in the past (e.g. issuance within last 10 years)
 * - `max_*_ahead` — date must be at most N units in the future (`0` = today, no future dates)
 *
 * Distinct from `min_months` / `max_months` + `start_date_ref`, which constrain a field
 * relative to another form date (contract length), not relative to today.
 */
export interface CountryFieldDisabledDates {
  min_years_ago?: number;
  max_years_ago?: number;
  max_years_ahead?: number;
}

export interface CountryField {
  name: string;
  label: string;
  required: boolean;
  type: DynamicFieldType;
  /**
   * Example text for free-text inputs, e.g. `"e.g. J92314056V"`.
   * Date and select fields ignore it.
   */
  placeholder?: string;
  step?: number; // Step index (add-employee.tsx STEPS enum). Default 0.
  digits?: boolean;
  letters?: boolean;
  uppercase?: boolean;
  spaces_allowed?: boolean;
  script?: CountryFieldScript; // Named writing-system preset. If set, defines charset and takes precedence over `letters` / `uppercase`
  email?: boolean;
  max_length?: number;
  min_length?: number;
  max_value?: number; // in combination with "type: text" and "digits: true" - maximum numeric value for the field
  min_value?: number; // in combination with "type: text" and "digits: true" - minimum numeric value for the field
  max_months?: number;
  min_months?: number;
  start_date_ref?: string;
  disabled_dates?: CountryFieldDisabledDates;
  override?: boolean;
  read_only?: boolean;
  default_value?: string | number;
  options?: Record<string, string | number>;
  conditional_required?: ConditionalRequired;
  conditional_depends?: ConditionalDepends;
  calculation?: ConditionalCalculation;
  date_range_transform?: DateRangeTransform;
  show_in_onboarding_only?: boolean;
}

export interface CountryFieldsResponse {
  [key: string]: {
    fields: CountryField[];
    field_notes?: CountryFieldNote[];
  };
}

export const COUNTRIES_WITH_DYNAMIC_PROBATION_MONTHS = ['ES', 'FR'] as const;

export type CountriesWithDynamicProbationMonths =
  (typeof COUNTRIES_WITH_DYNAMIC_PROBATION_MONTHS)[number];

export const COUNTRIES_WITH_CLASSIFICATION = ['AT'] as const;

export type CountriesWithClassification = (typeof COUNTRIES_WITH_CLASSIFICATION)[number];

export interface CountryProbationMonthsRequest {
  employment_job_id: number;
  gross_salary: number;
  contract_type?: string;
}

export interface CountryProbationMonthsOption {
  min_months: number;
  max_months: number;
  options: number[];
}

export interface CountryProbationMonthsResponse {
  probation_period_in_months: CountryProbationMonthsOption;
  probation_period_extension_in_months?: CountryProbationMonthsOption;
}

export interface CountryClassificationRequest {
  employment_job_id: number;
  professional_years: number;
  gross_salary: number;
}

export interface CountryClassificationResponse {
  job_family: string;
  advancement_level: string;
  professional_years: number;
  min_salary: number;
  gross_salary: number | null;
  auto_downgraded: boolean;
  note: string | null;
}
